import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Calendar } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import AxiosDB from "@/Config/AxiosDB";
import { TabsContent, TabsTrigger, Tabs, TabsList } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import Swal from "sweetalert2";

export default function ConfiguracionNCF({ user, onLogout }) {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("en_proceso");

  const [formData, setFormData] = useState({
    tipo_ncf: "",
    aprobacion_no: "",
    aprobado_desde: "",
    aprobado_hasta: "",
    fecha_aprobacion: "",
    vence: false,
    fecha_vencimiento: "",
  });

  useEffect(() => {
    loadConfiguraciones();
  }, []);

  const loadConfiguraciones = async () => {
    try {
      const response = await AxiosDB.get(`/configuracion-ncf`);
      setConfiguraciones(response.data);
    } catch (error) {
      toast.error("Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        tipo_ncf: config.tipo_ncf,
        aprobacion_no: config.aprobacion_no,
        aprobado_desde: config.aprobado_desde,
        aprobado_hasta: config.aprobado_hasta,
        fecha_aprobacion: config.fecha_aprobacion,
        vence: config.vence,
        fecha_vencimiento: config.fecha_vencimiento || "",
      });
    } else {
      setEditingConfig(null);
      setFormData({
        tipo_ncf: "",
        aprobacion_no: "",
        aprobado_desde: "",
        aprobado_hasta: "",
        fecha_aprobacion: "",
        vence: 0,
        fecha_vencimiento: "",
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.aprobado_desde.trim().length !== 13 || formData.aprobado_hasta.trim().length !== 13) {
      return toast.error("Numero de Comprobante Incompleto.");
    }
    if (formData.aprobado_desde.trim() > formData.aprobado_hasta.trim()) {
      return toast.error("Numero de Comprobante Hasta es Menor que Desde, Favor verifique.");
    }

    try {
      if (editingConfig) {
        await AxiosDB.put(`/configuracion-ncf/${editingConfig.comprobante_id}`, formData);
        toast.success("Configuración actualizada exitosamente");
      } else {
        await AxiosDB.post(`/configuracion-ncf`, formData);
        toast.success("Configuración creada exitosamente");
      }
      setModalOpen(false);
      loadConfiguraciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta configuración?")) {
      return;
    }

    try {
      await AxiosDB.delete(`${API}/configuracion-ncf/${id}`);
      toast.success("Configuración eliminada exitosamente");
      loadConfiguraciones();
    } catch (error) {
      toast.error("Error al eliminar configuración");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, vence: checked ? 1 : 0 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: es });
    } catch {
      return dateString;
    }
  };



  const ESTADOS = [
    { value: "en_proceso", label: "Procesando" },
    { value: "cola", label: "En Cola" },
    { value: "finalizado", label: "Finalizado" },
  ];

  const getEstado = (c) => c.estado || "en_espera";
  const contarPorEstado = (estado) => configuraciones.filter((c) => getEstado(c) === estado).length;

  const renderTabla = (estado) => {
    const lista = configuraciones.filter((c) => getEstado(c) === estado);
    if (lista.length === 0) {
      return (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid={`empty-${estado}`}>
          No hay secuencias en esta sección.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Tipo NCF</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Aprobación No.</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Desde</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Hasta</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Aprobación</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Vence</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Vencimiento</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Disponibilidad</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((config) => {
              const pct = getDisponibilidadPct(config);

              return (
                <tr key={config.id} className={`border-b border-slate-100 last:border-0 transition-colors ${getRowColorClass(config)}`} data-testid={`config-row-${config.id}`}>
                  <td className="py-3 px-4 text-sm font-semibold">{config.tipo_ncf}</td>
                  <td className="py-3 px-4 text-sm font-mono">{config.aprobacion_no}</td>
                  <td className="py-3 px-4 text-sm">{config.aprobado_desde}</td>
                  <td className="py-3 px-4 text-sm">{config.aprobado_hasta}</td>
                  <td className="py-3 px-4 text-sm">{formatDate(config.fecha_aprobacion)}</td>
                  <td className="py-3 px-4 text-center text-sm">
                    {config.vence === 1 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Sí</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">No</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">{config.vence === 1 ? formatDate(config.fecha_vencimiento) : "-"}</td>
                  <td className="py-3 px-4 text-sm"> {config.cantidad_autorizada} /{config.disponibilidad} </td>

                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openModal(config)}
                        data-testid={`edit-config-${config.comprobante_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {



                          const result = await Swal.fire({
                            title: "¿Está seguro?",
                            text: "Esta acción no se puede deshacer.",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Sí, continuar",
                            cancelButtonText: "Cancelar",
                          });

                          if (result.isConfirmed) {
                            // Ejecutar acción
                            handleDelete(config.comprobante_id)

                          }


                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-config-${config.comprobante_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            }
            )}
          </tbody>
        </table>
      </div>
    );
  };




  const getDisponibilidadPct = (c) => {
    if (c.estado === 'finalizado') return null;
    const aprobado = Number(c.cantidad_autorizada) || 0;
    const disponible = Number(c.disponibilidad) || 0;
    if (aprobado <= 0) return null;
    return (disponible / aprobado) * 100;
  };

  // Color de fila según % de disponibilidad vs aprobado
  const getRowColorClass = (c) => {
    const pct = getDisponibilidadPct(c);
    if (pct === null) return "";
    if (pct <= 5) return "bg-red-100 hover:bg-red-200/70";
    if (pct <= 10) return "bg-pink-100 hover:bg-pink-200/70";
    if (pct <= 20) return "bg-orange-100 hover:bg-orange-200/70";
    if (pct <= 30) return "bg-yellow-100 hover:bg-yellow-200/70";
    return "hover:bg-blue-50/40";
  };


  const validarNCF = (ncf, tipoNCF) => {
    const valor = (ncf || "").trim();

    return (
      valor.length === 13 &&              // Debe tener 13 caracteres
      valor.startsWith("E") &&            // Debe iniciar con E
      valor.startsWith(tipoNCF || "")     // Debe iniciar con el tipo seleccionado (E31, E32...)
    );
  };
  return (
    <Layout user={user} onLogout={onLogout} title="Configuración de NCF">


      <Card data-testid="configuracion-ncf-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Configuración de Comprobantes Fiscales</CardTitle>
              <CardDescription>
                Gestiona las secuencias autorizadas de NCF
              </CardDescription>
            </div>
            <Button onClick={() => openModal()} data-testid="add-config-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Configuración
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-600">Cargando...</div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-lg grid-cols-3">
                {ESTADOS.map((e) => (
                  <TabsTrigger key={e.value} value={e.value} data-testid={`tab-${e.value}`} className="gap-2">
                    {e.label}
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{contarPorEstado(e.value)}</Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
              {ESTADOS.map((e) => (
                <TabsContent key={e.value} value={e.value} className="mt-4">
                  {renderTabla(e.value)}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      </Card>



      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="config-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingConfig ? "Editar Configuración NCF" : "Nueva Configuración NCF"}
            </DialogTitle>
            <DialogDescription>
              {editingConfig ? "Modifica los datos de la configuración" : "Ingresa los datos de la nueva configuración"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo_ncf">Tipo NCF *</Label>
                  <Input
                    id="tipo_ncf"
                    name="tipo_ncf"
                    placeholder="Ej: B01, B02, E31"
                    value={formData.tipo_ncf}
                    onChange={handleChange}
                    required
                    data-testid="tipo-ncf-input"
                    style={{
                      color: formData.tipo_ncf.trim().length === 3 ? null : 'red',
                      borderColor: formData.tipo_ncf.trim().length === 3 ? null : 'red'
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aprobacion_no">Aprobación No. *</Label>
                  <Input
                    id="aprobacion_no"
                    name="aprobacion_no"
                    placeholder="Número de aprobación"
                    value={formData.aprobacion_no}
                    onChange={handleChange}
                    required
                    data-testid="aprobacion-no-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="aprobado_desde">Aprobado Desde *</Label>
                  <Input
                    id="aprobado_desde"
                    name="aprobado_desde"
                    placeholder="Ej: E3100000001"
                    value={formData.aprobado_desde}
                    onChange={handleChange}
                    required
                    data-testid="aprobado-desde-input"
                    style={{
                      color: validarNCF(formData.aprobado_desde, formData.tipo_ncf) ? null : 'red',
                      borderColor: validarNCF(formData.aprobado_desde, formData.tipo_ncf) ? null : 'red'
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aprobado_hasta">Aprobado Hasta *</Label>
                  <Input
                    id="aprobado_hasta"
                    name="aprobado_hasta"
                    placeholder="Ej: E3100010000"
                    value={formData.aprobado_hasta}
                    onChange={handleChange}
                    required
                    data-testid="aprobado-hasta-input"
                    style={{
                      color: validarNCF(formData.aprobado_hasta, formData.tipo_ncf) ? null : 'red',
                      borderColor: validarNCF(formData.aprobado_hasta, formData.tipo_ncf) ? null : 'red'
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_aprobacion">Fecha de Aprobación *</Label>
                <Input
                  id="fecha_aprobacion"
                  name="fecha_aprobacion"
                  type="date"
                  value={formData.fecha_aprobacion}
                  onChange={handleChange}
                  required
                  data-testid="fecha-aprobacion-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label>¿Vence?</Label>
                    <p className="text-sm text-slate-500">Indica si esta secuencia de NCF tiene fecha de vencimiento</p>
                  </div>
                  <Switch
                    checked={formData.vence === 1}
                    onCheckedChange={handleSwitchChange}
                    data-testid="vence-switch"
                  />
                </div>
              </div>

              {formData.vence && (
                <div className="space-y-2">
                  <Label htmlFor="fecha_vencimiento">Fecha de Vencimiento *</Label>
                  <Input
                    id="fecha_vencimiento"
                    name="fecha_vencimiento"
                    type="date"
                    value={formData.fecha_vencimiento}
                    onChange={handleChange}
                    required={formData.vence}
                    data-testid="fecha-vencimiento-input"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" data-testid="submit-config-button" disabled={(!validarNCF(formData.aprobado_hasta, formData.tipo_ncf) || !validarNCF(formData.aprobado_desde, formData.tipo_ncf))}>
                {editingConfig ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}